/**
 * Admin → Product Management → Add-ons
 *
 * CRUD over add-on products (orb, story packs, book, vault). Compatibility
 * selector scopes an add-on to specific offerings (empty = all offerings).
 *
 * Design: docs/technical-design-website-product.md §4.2
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Edit, Loader2, Power, PowerOff } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import type { AdminAddonProduct, AdminProductOffering, CatalogStatus } from '@/types';

interface AddonFormData {
  slug: string;
  name: string;
  description: string;
  priceDollars: string;
  // Checked offering ids. Empty selection = compatible with ALL offerings.
  compatibleOfferingIds: string[];
  availablePostPurchase: boolean;
  displayOrder: string;
  status: CatalogStatus;
}

const emptyForm: AddonFormData = {
  slug: '',
  name: '',
  description: '',
  priceDollars: '',
  compatibleOfferingIds: [],
  availablePostPurchase: true,
  displayOrder: '0',
  status: 'available',
};

const formatPrice = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function AddonProductsPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<AdminAddonProduct | null>(null);
  const [form, setForm] = useState<AddonFormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline banner above the table for failed activate/deactivate (review F11).
  const [tableError, setTableError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const {
    data: addons,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'addon-products'],
    queryFn: async () => {
      const response = await apiClient.get<AdminAddonProduct[]>('/api/v1/admin/products/addons');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Offerings for the compatibility selector + name lookups in the table.
  const { data: offerings } = useQuery({
    queryKey: ['admin', 'product-offerings'],
    queryFn: async () => {
      const response = await apiClient.get<AdminProductOffering[]>(
        '/api/v1/admin/products/offerings'
      );
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const offeringName = (id: string) => offerings?.find((o) => o.id === id)?.name ?? id.slice(0, 8);

  const set = <K extends keyof AddonFormData>(key: K, value: AddonFormData[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleCreate = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setSelected(null);
    setError(null);
    setFormOpen(true);
  };

  const handleEdit = (addon: AdminAddonProduct) => {
    setForm({
      slug: addon.slug,
      name: addon.name,
      description: addon.description ?? '',
      priceDollars: (addon.priceCents / 100).toString(),
      compatibleOfferingIds: addon.compatibleOfferingIds ?? [],
      availablePostPurchase: addon.availablePostPurchase,
      displayOrder: addon.displayOrder.toString(),
      status: addon.status ?? 'available',
    });
    setIsEditing(true);
    setSelected(addon);
    setError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const payload = {
      name: form.name,
      description: form.description || null,
      priceCents: Math.round(parseFloat(form.priceDollars) * 100),
      compatibleOfferingIds:
        form.compatibleOfferingIds.length > 0 ? form.compatibleOfferingIds : null,
      availablePostPurchase: form.availablePostPurchase,
      displayOrder: parseInt(form.displayOrder, 10) || 0,
      status: form.status,
    };

    const response =
      isEditing && selected
        ? await apiClient.patch(`/api/v1/admin/products/addons/${selected.id}`, payload)
        : await apiClient.post('/api/v1/admin/products/addons', { ...payload, slug: form.slug });

    setIsSaving(false);
    if (!response.success) {
      setError(response.error?.message || 'Save failed');
      return;
    }
    setFormOpen(false);
    setTableError(null);
    refetch();
    // Public catalog caches (funnel add-on lists in this app) must not serve
    // stale data after an admin edit (review F12).
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  const handleToggleActive = async (addon: AdminAddonProduct) => {
    setTogglingId(addon.id);
    const action = addon.isActive ? 'deactivate' : 'activate';
    const response = await apiClient.post(`/api/v1/admin/products/addons/${addon.id}/${action}`);
    setTogglingId(null);
    if (!response.success) {
      setTableError(
        response.error?.message || `Failed to ${action} "${addon.name}". Please try again.`
      );
      return;
    }
    setTableError(null);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  const toggleCompat = (id: string) =>
    setForm((f) => ({
      ...f,
      compatibleOfferingIds: f.compatibleOfferingIds.includes(id)
        ? f.compatibleOfferingIds.filter((x) => x !== id)
        : [...f.compatibleOfferingIds, id],
    }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Add-on Products</h1>
          <p className="text-muted-foreground">
            Optional products attachable to an offering (orb, story packs, book, vault).
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Add-on
        </Button>
      </div>

      {tableError && <p className="text-sm text-destructive">{tableError}</p>}

      <Card>
        <CardHeader>
          <CardTitle>All add-ons</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Compatible with</TableHead>
                  <TableHead>Post-purchase</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(addons ?? []).map((addon) => (
                  <TableRow key={addon.id}>
                    <TableCell>{addon.displayOrder}</TableCell>
                    <TableCell className="font-medium">{addon.name}</TableCell>
                    <TableCell className="font-mono text-xs">{addon.slug}</TableCell>
                    <TableCell>{formatPrice(addon.priceCents)}</TableCell>
                    <TableCell className="text-xs">
                      {addon.compatibleOfferingIds === null
                        ? 'All offerings'
                        : addon.compatibleOfferingIds.map(offeringName).join(', ')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={addon.availablePostPurchase ? 'default' : 'secondary'}>
                        {addon.availablePostPurchase ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant={addon.isActive ? 'default' : 'secondary'}>
                          {addon.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {addon.status === 'coming_soon' && (
                          <Badge variant="outline">Coming soon</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(addon)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={togglingId === addon.id}
                        onClick={() => handleToggleActive(addon)}
                        title={addon.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {togglingId === addon.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : addon.isActive ? (
                          <PowerOff className="h-4 w-4" />
                        ) : (
                          <Power className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(addons ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No add-ons yet. Run the catalog seed or create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit add-on' : 'New add-on'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'The slug is immutable.' : 'Slug cannot be changed after creation.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="addon-slug">Slug</Label>
                <Input
                  id="addon-slug"
                  value={form.slug}
                  disabled={isEditing}
                  placeholder="physical-orb"
                  onChange={(e) => set('slug', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addon-name">Name</Label>
                <Input
                  id="addon-name"
                  value={form.name}
                  placeholder="Physical Legacy Orb"
                  onChange={(e) => set('name', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="addon-description">Description</Label>
              <Input
                id="addon-description"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="addon-price">Price (USD)</Label>
                <Input
                  id="addon-price"
                  type="number"
                  step="0.01"
                  value={form.priceDollars}
                  placeholder="299"
                  onChange={(e) => set('priceDollars', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addon-order">Display order</Label>
                <Input
                  id="addon-order"
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) => set('displayOrder', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Availability</Label>
              {/* Coming-soon add-ons stay visible in the funnel but cannot be
                  added to an order (ClickUp 86bar0jc1). */}
              <Select value={form.status} onValueChange={(v) => set('status', v as CatalogStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="coming_soon">Coming soon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Compatible offerings (none checked = all offerings)</Label>
              <div className="space-y-1 rounded-md border p-3">
                {(offerings ?? []).map((offering) => (
                  <label key={offering.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.compatibleOfferingIds.includes(offering.id)}
                      onChange={() => toggleCompat(offering.id)}
                    />
                    {offering.name}
                    <span className="text-xs text-muted-foreground">({offering.slug})</span>
                  </label>
                ))}
                {(offerings ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No offerings exist yet.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="addon-post-purchase"
                type="checkbox"
                checked={form.availablePostPurchase}
                onChange={(e) => set('availablePostPurchase', e.target.checked)}
              />
              <Label htmlFor="addon-post-purchase">
                Available post-purchase (shown in Account page "Add Products")
              </Label>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !form.name || !form.priceDollars || (!isEditing && !form.slug)}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save changes' : 'Create add-on'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
